import React, { useEffect, useRef } from 'react';

interface XmlMergerProps {
    files: FileList | null;
    onMerge: (xmlDoc: Document | null, tocContent: string | null) => void;
}

const XmlMerger: React.FC<XmlMergerProps> = ({ files, onMerge }) => {
    const workerRef = useRef<Worker | null>(null);
    // onMergeはインライン関数で毎レンダリング新しい参照になるため、
    // useRefで最新を保持しuseEffectの依存配列から外す
    const onMergeRef = useRef(onMerge);
    onMergeRef.current = onMerge;

    useEffect(() => {
        // 前回のWorkerが残っていれば終了させる
        if (workerRef.current) {
            workerRef.current.terminate();
            workerRef.current = null;
        }

        if (!files || files.length === 0) {
            onMergeRef.current(null, null);
            return;
        }

        let cancelled = false;

        (async () => {
            // FileListからArrayBufferを読み出す
            const filesData = await Promise.all(
                Array.from(files).map(async (file) => {
                    const arrayBuffer = await file.arrayBuffer();
                    return { name: file.name, arrayBuffer };
                })
            );

            if (cancelled) return;

            // Web WorkerでZIP解凍を実行（メインスレッドをブロックしない）
            const worker = new Worker(
                new URL('./xmlMerger.worker.ts', import.meta.url),
                { type: 'module' }
            );
            workerRef.current = worker;

            worker.onmessage = (e) => {
                workerRef.current = null;
                worker.terminate();

                if (cancelled) return;

                const { fileResults } = e.data;

                if (!fileResults || fileResults.length === 0) {
                    onMergeRef.current(null, null);
                    return;
                }

                // DOMParser はメインスレッドで使用
                const parser = new DOMParser();
                const serializer = new XMLSerializer();
                const mergedRoot = document.implementation.createDocument(null, "mergedDocuments", null);
                let mergedToc = "";

                fileResults.forEach((r: { xmlContent: string | null; toc: string | null; name: string }) => {
                    if (r.xmlContent) {
                        const xmlDoc = parser.parseFromString(r.xmlContent, 'text/xml');

                        // TOC抽出
                        const tocNodes = xmlDoc.getElementsByTagName('w:sdt');
                        if (tocNodes.length > 0) {
                            const tocContent = Array.from(tocNodes).map(node => serializer.serializeToString(node)).join('\n');
                            mergedToc += tocContent + "\n";
                        }

                        const imported = mergedRoot.importNode(xmlDoc.documentElement, true);
                        imported.setAttribute("source", r.name);
                        mergedRoot.documentElement.appendChild(imported);
                    }
                });

                onMergeRef.current(mergedRoot, mergedToc || null);
            };

            worker.onerror = () => {
                workerRef.current = null;
                worker.terminate();
                if (!cancelled) {
                    onMergeRef.current(null, null);
                }
            };

            // ArrayBufferをWorkerに転送（transferableで高速）
            const transferables = filesData.map(f => f.arrayBuffer);
            worker.postMessage(filesData, transferables);
        })();

        return () => {
            cancelled = true;
            if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            }
        };
    }, [files]);

    return null;
};

export default XmlMerger;
